/**
 * useAdminPicks Hook
 * Manages infinite scroll state, filtering, sorting, and data fetching
 * for the Picks Data Dashboard
 */

import type {
  FetchParlaysResult,
  FetchPicksResult,
  GroupBy,
  ParlayRecord,
  ParlaySort,
  PickRecord,
  PicksFilters,
  SortBy,
  SortDirection,
  ViewMode,
} from '@server/services/admin/adminPicksTypes.ts';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseAdminPicksParams {
  viewMode: ViewMode;
  groupBy: GroupBy;
  sortBy: SortBy | ParlaySort;
  sortDirection: SortDirection;
  filters: PicksFilters;
  limit?: number;
}

export interface UseAdminPicksResult {
  picks: PickRecord[];
  parlays: ParlayRecord[];
  cursor: string | null;
  hasMore: boolean;
  total: number;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  loadMore: () => void;
  refetch: () => void;
  setSortBy: (field: SortBy | ParlaySort) => void;
  setSortDirection: (dir: SortDirection) => void;
}

/**
 * Hook to fetch and manage picks data with infinite scroll
 * Debounces search filter changes by 300ms
 */
export function useAdminPicks(params: UseAdminPicksParams): UseAdminPicksResult {
  const { viewMode, groupBy, sortBy, sortDirection, filters, limit = 50 } = params;

  const [picks, setPicks] = useState<PickRecord[]>([]);
  const [parlays, setParlays] = useState<ParlayRecord[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track if we're currently fetching to prevent duplicate requests
  const isFetchingRef = useRef(false);

  // Debounce timer for search filter
  const debounceTimerRef = useRef<number | undefined>(undefined);

  // Track previous params to detect changes
  const prevParamsRef = useRef<string>('');

  /**
   * Fetch picks from API
   */
  const fetchPicks = useCallback(
    async (cursorParam?: string) => {
      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      const isLoadingMore = Boolean(cursorParam);

      try {
        if (isLoadingMore) {
          setLoadingMore(true);
        } else {
          setLoading(true);
          setError(null);
        }

        // Build query params
        const params = new URLSearchParams({
          viewMode,
          groupBy,
          limit: String(limit),
          sortBy,
          sortDirection,
        });

        if (cursorParam) {
          params.set('cursor', cursorParam);
        }

        if (Object.keys(filters).length > 0) {
          params.set('filters', JSON.stringify(filters));
        }

        const response = await fetch(`/api/admin/picks?${params.toString()}`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch picks: ${response.statusText}`);
        }

        const result: FetchPicksResult | FetchParlaysResult = await response.json();

        // Update state based on groupBy
        if (groupBy === 'parlay') {
          const parlayResult = result as FetchParlaysResult;
          if (isLoadingMore) {
            setParlays((prev) => [...(prev || []), ...parlayResult.parlays]);
          } else {
            setParlays(parlayResult.parlays);
            setPicks([]); // Clear picks when switching to parlay view
          }
        } else {
          const pickResult = result as FetchPicksResult;
          if (isLoadingMore) {
            setPicks((prev) => [...(prev || []), ...pickResult.picks]);
          } else {
            setPicks(pickResult.picks);
            setParlays([]); // Clear parlays when switching to leg view
          }
        }

        setCursor(result.cursor);
        setHasMore(result.hasMore);
        setTotal(result.total);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch picks');
      } finally {
        setLoading(false);
        setLoadingMore(false);
        isFetchingRef.current = false;
      }
    },
    [viewMode, groupBy, sortBy, sortDirection, filters, limit],
  );

  /**
   * Load more picks (next page)
   */
  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore || !cursor) {
      return;
    }

    fetchPicks(cursor);
  }, [hasMore, loadingMore, cursor, fetchPicks]);

  /**
   * Refetch from beginning (clears existing picks/parlays)
   */
  const refetch = useCallback(() => {
    setPicks([]);
    setParlays([]);
    setCursor(null);
    setHasMore(false);
    fetchPicks();
  }, [fetchPicks]);

  /**
   * Update sort field (triggers refetch)
   */
  const setSortByHandler = useCallback((_field: SortBy | ParlaySort) => {
    // This will be handled by parent component state
    // Refetch will be triggered by useEffect when params change
  }, []);

  /**
   * Update sort direction (triggers refetch)
   */
  const setSortDirectionHandler = useCallback((_dir: SortDirection) => {
    // This will be handled by parent component state
    // Refetch will be triggered by useEffect when params change
  }, []);

  /**
   * Detect parameter changes and refetch
   * Debounce search filter changes
   */
  useEffect(() => {
    const currentParams = JSON.stringify({
      viewMode,
      groupBy,
      sortBy,
      sortDirection,
      filters,
      limit,
    });

    // Check if params actually changed
    if (currentParams === prevParamsRef.current) {
      return;
    }

    prevParamsRef.current = currentParams;

    // Clear debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce if search filter changed (300ms)
    const prevFilters = prevParamsRef.current ? JSON.parse(prevParamsRef.current).filters : {};
    const searchChanged = filters.search !== prevFilters.search;

    if (searchChanged && filters.search) {
      debounceTimerRef.current = window.setTimeout(() => {
        refetch();
      }, 300);
    } else {
      // Immediate refetch for other changes
      refetch();
    }

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [viewMode, groupBy, sortBy, sortDirection, filters, limit, refetch]);

  return {
    picks,
    parlays,
    cursor,
    hasMore,
    total,
    loading,
    loadingMore,
    error,
    loadMore,
    refetch,
    setSortBy: setSortByHandler,
    setSortDirection: setSortDirectionHandler,
  };
}
